"use client";

import { useEffect, useState } from "react";
import { useLocation, NavLink } from "react-router-dom";
import PropTypes from "prop-types";
import List from "@mui/material/List";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SidenavCollapse from "examples/Sidenav/SidenavCollapse";
import SidenavList from "examples/Sidenav/SidenavList";
import SidenavItem from "examples/Sidenav/SidenavItem";
import SidenavRoot from "examples/Sidenav/SidenavRoot";
import sidenavLogoLabel from "examples/Sidenav/styles/sidenav";
import { useSoftUIController, setMiniSidenav } from "context";
import theme from "assets/theme";
import { useTranslation } from "react-i18next";
import OrdersApi from "api/orders";

function Sidenav({ color = "info", brand = "", brandName, routes, ...rest }) {
  const [openCollapse, setOpenCollapse] = useState(false);
  const [openNestedCollapse, setOpenNestedCollapse] = useState(false);
  const [controller, dispatch] = useSoftUIController();
  const { miniSidenav, transparentSidenav } = controller;
  const location = useLocation();
  const { pathname } = location;
  const collapseName = pathname.split("/").slice(1)[0];
  const itemName = pathname.split("/").slice(1)[1];
  const { t } = useTranslation();

  const warehouseName = localStorage.getItem("warehouseName");
  const manager = localStorage.getItem("manager");

  const [draftOrdersCount, setDraftOrdersCount] = useState(0);

  useEffect(() => {
    const handleStorageChange = () => {
      const newCount = Number(localStorage.getItem("draftOrdersCount") || 0);
      setDraftOrdersCount(newCount);
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("draftCountUpdated", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("draftCountUpdated", handleStorageChange);
    };
  }, []);

  useEffect(() => {
    const fetchDraftOrders = () => {
      const token = localStorage.getItem("token");
      if (!token) return;
      try {
        OrdersApi.GetDraftOrders(token, 1)
          .then((res) => {
            setDraftOrdersCount(res.data?.count ?? 0);
          })
          .catch((err) => {
            console.error("Draft orders error:", err);
          });
      } catch (err) {
        console.error(err);
      }
    };
    fetchDraftOrders();
    const interval = setInterval(fetchDraftOrders, 60000);
    return () => clearInterval(interval);
  }, []);

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  useEffect(() => {
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
    }
    window.addEventListener("resize", handleMiniSidenav);
    handleMiniSidenav();
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location]);

  const renderNestedCollapse = (collapse) => {
    const template = collapse.map(({ name, route, key, href }) =>
      href ? (
        <Link
          key={key}
          href={href}
          target="_blank"
          rel="noreferrer"
          sx={{ textDecoration: "none" }}
        >
          <SidenavItem name={name} nested />
        </Link>
      ) : (
        <NavLink to={route} key={key} sx={{ textDecoration: "none" }}>
          <SidenavItem name={name} active={route === pathname} nested />
        </NavLink>
      )
    );
    return template;
  };

  const renderCollapse = (collapses) =>
    collapses.map(({ name, collapse, route, href, key }) => {
      let returnValue;
      if (collapse) {
        returnValue = (
          <SidenavItem
            key={key}
            name={name}
            active={key === itemName}
            open={openNestedCollapse === name}
            onClick={() =>
              openNestedCollapse === name
                ? setOpenNestedCollapse(false)
                : setOpenNestedCollapse(name)
            }
          >
            {renderNestedCollapse(collapse)}
          </SidenavItem>
        );
      } else {
        returnValue = href ? (
          <Link
            href={href}
            key={key}
            target="_blank"
            rel="noreferrer"
            sx={{ textDecoration: "none" }}
          >
            <SidenavItem name={name} active={key === itemName} />
          </Link>
        ) : (
          <NavLink to={route} key={key} sx={{ textDecoration: "none" }}>
            <SidenavItem name={name} active={key === itemName} />
          </NavLink>
        );
      }
      return <SidenavList key={key}>{returnValue}</SidenavList>;
    });

  const renderRoutes = routes.map(
    ({ type, name, icon, title, collapse, noCollapse, key, href, route }) => {
      let returnValue;
      if (type === "collapse") {
        if (href) {
          returnValue = (
            <Link
              href={href}
              key={key}
              target="_blank"
              rel="noreferrer"
              sx={{ textDecoration: "none" }}
            >
              <SidenavCollapse
                name={name}
                icon={icon}
                active={key === collapseName}
                noCollapse={noCollapse}
                draftOrdersCount={draftOrdersCount}
              />
            </Link>
          );
        } else if (noCollapse && route) {
          returnValue = (
            <NavLink to={route} key={key}>
              <SidenavCollapse
                name={name}
                icon={icon}
                noCollapse={noCollapse}
                active={key === collapseName}
                draftOrdersCount={draftOrdersCount}
              >
                {collapse ? renderCollapse(collapse) : null}
              </SidenavCollapse>
            </NavLink>
          );
        } else {
          returnValue = (
            <SidenavCollapse
              key={key}
              name={name}
              icon={icon}
              active={key === collapseName}
              open={openCollapse === key}
              onClick={() =>
                openCollapse === key
                  ? setOpenCollapse(false)
                  : setOpenCollapse(key)
              }
              draftOrdersCount={draftOrdersCount}
            >
              {collapse ? renderCollapse(collapse) : null}
            </SidenavCollapse>
          );
        }
      } else if (type === "title") {
        returnValue = (
          <SoftTypography
            key={key}
            display="block"
            variant="caption"
            fontWeight="bold"
            textTransform="uppercase"
            opacity={0.6}
            pl={3}
            mt={2}
            mb={1}
            ml={1}
          >
            {title}
          </SoftTypography>
        );
      } else if (type === "divider") {
        returnValue = <Divider key={key} />;
      }
      return returnValue;
    }
  );

  return (
    <SidenavRoot
      {...rest}
      variant="permanent"
      ownerState={{ transparentSidenav, miniSidenav }}
    >
      {/* Mobile overlay backdrop */}
      {miniSidenav && (
        <Box
          onClick={closeSidenav}
          sx={{
            display: { xs: "block", xl: "none" },
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            bgcolor: "rgba(0,0,0,0.5)",
            zIndex: 1199,
          }}
        />
      )}
      <SoftBox
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        height="100%"
        pt={3}
        pb={1}
      >
        {/* Top content: Logo and Navigation */}
        <SoftBox>
          <SoftBox
            display={{ xs: "block", xl: "none" }}
            position="absolute"
            top={0}
            right={0}
            p={1.625}
            onClick={closeSidenav}
            sx={{ cursor: "pointer" }}
            aria-label="Close Sidenav"
          >
            <SoftTypography variant="h6" color="secondary">
              <Icon sx={{ fontWeight: "bold" }}>close</Icon>
            </SoftTypography>
          </SoftBox>
          <SoftBox
            component={NavLink}
            to="/"
            display="flex"
            alignItems="center"
            px={4}
          >
            {brand && (
              <SoftBox
                component="img"
                src={brand}
                alt="Logo"
                width="32px"
                height="32px"
              />
            )}
            <SoftBox
              width={!brandName && "100%"}
              sx={(theme) => sidenavLogoLabel(theme, { miniSidenav })}
            >
              <SoftTypography
                component="h6"
                variant="button"
                fontWeight="medium"
              >
                {t(brandName)}
              </SoftTypography>
            </SoftBox>
          </SoftBox>
          <Divider />
          <List sx={{ marginLeft: -2 }}>{renderRoutes}</List>
        </SoftBox>

        {(manager || warehouseName) && (
          <SoftBox
            display="flex"
            flexDirection="column"
            gap={1.5}
            p={1.5}
            mx={1}
            mb={1}
          >
            {/* Manager Card */}
            {manager && (
              <SoftBox
                display="flex"
                alignItems="center"
                p={1.5}
                bgcolor="white"
                borderRadius="lg"
                boxShadow="0px 2px 8px rgba(0, 0, 0, 0.08)"
                sx={{
                  transition: "all 0.3s ease",
                  "&:hover": {
                    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.12)",
                    transform: "translateY(-1px)",
                  },
                }}
              >
                {/* Avatar with gradient background */}
                <SoftBox
                  position="relative" // needed for the online badge
                  width={44}
                  height={44}
                  borderRadius="50%"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  fontWeight="bold"
                  sx={{
                    fontSize: "1.2rem",
                    boxShadow: `0px 2px 8px ${theme.palette.info.main}40`,
                  }}
                >
                  {manager.charAt(0).toUpperCase()}

                  {/* Green online circle */}
                  <SoftBox
                    position="absolute"
                    top={0}
                    right={0}
                    width={12}
                    height={12}
                    borderRadius="50%"
                    bgColor="#28a745"
                    border="2px solid white" // optional: white border to make it stand out
                  />
                </SoftBox>
                {!miniSidenav && (
                  <SoftBox flexShrink={1} minWidth={0}>
                    {/* use flexShrink instead of flex=1 */}
                    <SoftTypography
                      variant="button"
                      fontWeight="bold"
                      color="dark"
                      width={190}
                      ml={2}
                      sx={{
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                        display: "inline-block", // prevents extra flex space
                        lineHeight: 1.2, // tighter lines
                      }}
                    >
                      {manager}
                    </SoftTypography>
                  </SoftBox>
                )}
              </SoftBox>
            )}

            {/* Warehouse Card */}
            {warehouseName && (
              <SoftBox
                p={1.5}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                background={`linear-gradient(135deg, ${theme.palette[color]?.main}, ${theme.palette[color]?.light})`}
                borderRadius="lg"
                boxShadow={`0px 2px 8px ${theme.palette[color]?.main}40`}
                border={`1px solid ${theme.palette[color]?.light}80`}
                sx={{
                  transition: "all 0.3s ease",
                  "&:hover": {
                    boxShadow: `0px 4px 12px ${theme.palette[color]?.main}60`,
                    transform: "translateY(-1px)",
                  },
                }}
              >
                <SoftBox display="flex" alignItems="center" minWidth={0}>
                  <Icon
                    sx={{
                      color: "white",
                      fontSize: { xs: "2rem", lg: "2.2rem" },
                      mr: 1.5,
                      flexShrink: 0,
                    }}
                  >
                    warehouse
                  </Icon>
                  {!miniSidenav && (
                    <SoftBox minWidth={0}>
                      <SoftTypography
                        component="h6"
                        variant="caption"
                        fontWeight="medium"
                        opacity={0.8}
                      >
                        {t("Responsable de")}:
                      </SoftTypography>
                      <SoftTypography variant="button" fontWeight="bold" noWrap>
                        {warehouseName}
                      </SoftTypography>
                    </SoftBox>
                  )}
                </SoftBox>
              </SoftBox>
            )}
          </SoftBox>
        )}
      </SoftBox>
    </SidenavRoot>
  );
}

Sidenav.propTypes = {
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "dark",
  ]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
